import { useEffect, useState } from "react";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { CustomButton } from "@/components/common/customButton";
import { CustomTextInput } from "@/components/common/inputFields/customTextInput";

export type GenerateMarketSummaryForm = {
  country: string;
  sector: string;
  topic: string;
};

interface GenerateMarketSummaryModalProps {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: GenerateMarketSummaryForm) => void;
}

export function GenerateMarketSummaryModal({
  open,
  submitting,
  onClose,
  onSubmit,
}: GenerateMarketSummaryModalProps) {
  const [country, setCountry] = useState("");
  const [sector, setSector] = useState("");
  const [topic, setTopic] = useState("");

  useEffect(() => {
    if (open) {
      setCountry("");
      setSector("");
      setTopic("");
    }
  }, [open]);

  const canSubmit =
    country.trim().length > 0 && sector.trim().length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      country: country.trim(),
      sector: sector.trim(),
      topic: topic.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate market summary</DialogTitle>
      <DialogContent>
        <BoxFields
          country={country}
          sector={sector}
          topic={topic}
          onCountryChange={setCountry}
          onSectorChange={setSector}
          onTopicChange={setTopic}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton
          text="Cancel"
          handleButtonPress={onClose}
          isButtonDisabled={submitting}
          solid={false}
        />
        <CustomButton
          text={submitting ? "Generating…" : "Generate"}
          handleButtonPress={handleSubmit}
          isButtonDisabled={!canSubmit}
          isLoading={submitting}
        />
      </DialogActions>
    </Dialog>
  );
}

function BoxFields({
  country,
  sector,
  topic,
  onCountryChange,
  onSectorChange,
  onTopicChange,
  disabled,
}: {
  country: string;
  sector: string;
  topic: string;
  onCountryChange: (value: string) => void;
  onSectorChange: (value: string) => void;
  onTopicChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 12 }}>
      <CustomTextInput
        label="Country"
        value={country}
        onChange={(value) => onCountryChange(String(value))}
        type="text"
        disabled={disabled}
      />
      <CustomTextInput
        label="Sector"
        value={sector}
        onChange={(value) => onSectorChange(String(value))}
        type="text"
        disabled={disabled}
      />
      <CustomTextInput
        label="Topic (optional)"
        value={topic}
        onChange={(value) => onTopicChange(String(value))}
        type="text"
        disabled={disabled}
      />
      <Alert severity="warning" sx={{ mt: 0.5 }}>
        If few reports match, the summary may be incomplete. Upload more for
        this market or refresh when new data is available.
      </Alert>
    </div>
  );
}
